import { useEffect, useRef, useState } from "react";

/** Soft keyboards are taller than the Safari URL-bar collapse (~60–100px). */
const KEYBOARD_HEIGHT_THRESHOLD_PX = 150;
/** Pinch-zoom also shrinks `visualViewport.height`. Ignore that. */
const MAX_SCALE = 1.05;
/** Field-to-field focus moves can report a full viewport for one frame. */
const HIDE_RELEASE_DELAY_MS = 250;

const TYPING_TAGS = new Set(["INPUT", "TEXTAREA", "IFRAME"]);

export interface VirtualKeyboardSnapshot {
  activeElementTag: string | null;
  contentEditable: boolean;
  innerHeight: number;
  visualViewportHeight: number | null;
  visualViewportScale: number | null;
}

/**
 * True only when a text entry is focused AND the visual viewport shrank by
 * more than a soft keyboard. Desktop focus does not shrink the viewport, so
 * it stays false there.
 */
export function isVirtualKeyboardOpen(snapshot: VirtualKeyboardSnapshot): boolean {
  const { visualViewportHeight, visualViewportScale } = snapshot;
  if (visualViewportHeight == null || visualViewportScale == null) return false;

  const typing =
    snapshot.contentEditable ||
    (snapshot.activeElementTag != null && TYPING_TAGS.has(snapshot.activeElementTag));
  if (!typing) return false;
  if (visualViewportScale >= MAX_SCALE) return false;

  return snapshot.innerHeight - visualViewportHeight > KEYBOARD_HEIGHT_THRESHOLD_PX;
}

function readSnapshot(): VirtualKeyboardSnapshot {
  const el = document.activeElement as HTMLElement | null;
  const viewport = window.visualViewport;
  return {
    activeElementTag: el?.tagName ?? null,
    contentEditable: Boolean(el?.isContentEditable),
    innerHeight: window.innerHeight,
    visualViewportHeight: viewport ? viewport.height : null,
    visualViewportScale: viewport ? viewport.scale : null,
  };
}

export function useVirtualKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  const openRef = useRef(false);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    let releaseTimer: ReturnType<typeof setTimeout> | undefined;

    const compute = () => {
      const next = isVirtualKeyboardOpen(readSnapshot());
      if (next) {
        if (releaseTimer) {
          clearTimeout(releaseTimer);
          releaseTimer = undefined;
        }
        if (!openRef.current) {
          openRef.current = true;
          setOpen(true);
        }
        return;
      }
      if (!openRef.current || releaseTimer) return;
      releaseTimer = setTimeout(() => {
        releaseTimer = undefined;
        openRef.current = false;
        setOpen(false);
      }, HIDE_RELEASE_DELAY_MS);
    };

    viewport.addEventListener("resize", compute);
    window.addEventListener("focusin", compute);
    window.addEventListener("focusout", compute);

    return () => {
      if (releaseTimer) clearTimeout(releaseTimer);
      viewport.removeEventListener("resize", compute);
      window.removeEventListener("focusin", compute);
      window.removeEventListener("focusout", compute);
    };
  }, []);

  return open;
}
